

import { useState, useMemo, useEffect } from "react";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import Badge from "../components/Badge";
import Modal from "../components/Modal";
import InvoiceForm from "./InvoiceForm";
import { invoicesApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useSettings } from "@/context/SettingsContext";
import { formatCurrency, formatCurrencyFull } from "@/lib/data";
import { sendInvoiceEmail } from "@/lib/emailService";
import { byNewest } from "@/lib/utils";
import {
  Plus,
  Search,
  FileText,
  Download,
  Mail,
  CheckCircle2,
  Clock,
  AlertCircle,
  Eye,
  Edit3,
  CreditCard,
  Printer,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Sheet,
} from "lucide-react";

type InvSortCol = "invoice" | "client" | "date" | "total" | "paid" | "balance";

function InvSortIcon({ col, sortCol, sortDir }: { col: InvSortCol; sortCol: InvSortCol | null; sortDir: "asc" | "desc" }) {
  if (sortCol !== col) return <ChevronsUpDown className="inline w-3 h-3 ml-0.5 opacity-30" />;
  return sortDir === "asc"
    ? <ChevronUp className="inline w-3 h-3 ml-0.5 text-amber-500" />
    : <ChevronDown className="inline w-3 h-3 ml-0.5 text-amber-500" />;
}

function InvSortTh({ col, label, align = "left", sortCol, sortDir, onSort }: {
  col: InvSortCol; label: string; align?: "left" | "right" | "center";
  sortCol: InvSortCol | null; sortDir: "asc" | "desc"; onSort: (c: InvSortCol) => void;
}) {
  return (
    <th
      onClick={() => onSort(col)}
      className={`text-${align} px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase cursor-pointer select-none hover:bg-stone-100 transition-colors`}
    >
      {label}<InvSortIcon col={col} sortCol={sortCol} sortDir={sortDir} />
    </th>
  );
}
import PrintableInvoice from "./components/PrintableInvoice";

const statuses = ["all", "unpaid", "partial", "overdue", "paid", "cancelled"] as const;
type Status = typeof statuses[number];
const STATUS_LABEL: Record<string, string> = {
  all: "All", unpaid: "Unpaid", partial: "Partly paid", overdue: "Overdue", paid: "Paid", cancelled: "Cancelled",
};
const matchesStatus = (inv: { status: string; is_overdue?: boolean }, s: Status) =>
  s === "all" ? true : s === "overdue" ? Boolean(inv.is_overdue) : inv.status === s;

interface Invoice {
  id: number;
  invoice_number: string;
  event_id: number;
  client_id: number;
  client?: { id: number; name: string; phone?: string; email?: string };
  event?: { 
    id: number; 
    name: string; 
    event_type?: string; 
    event_date?: string; 
    venue?: string;
    event_vendors?: EventVendor[];
  };
  total_amount: number;
  discount_amount: number;
  tax_amount: number;
  final_amount: number;
  paid_amount: number;
  balance_amount: number;
  /** Due date passed and money still owed (worked out by the server) */
  is_overdue?: boolean;
  terms?: string;
  status: string;
  due_date: string;
  invoice_date: string;
  notes: string;
  created_at?: string;
}

interface EventVendor {
  id: number;
  vendor_id: number;
  vendor?: {
    id: number;
    name: string;
    phone?: string;
    email?: string;
  };
  service_name: string;
  quantity: number;
  unit_price: number;
  total_amount: number;
  notes?: string;
}

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { settings } = useSettings();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<Status>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  
  // Email modal state
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailingInvoice, setEmailingInvoice] = useState<Invoice | null>(null);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [emailStatus, setEmailStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [emailError, setEmailError] = useState("");

  // Print invoice state
  const [printInvoice, setPrintInvoice] = useState<Invoice | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  // Sort state
  const [sortCol, setSortCol] = useState<InvSortCol | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const handleSort = (col: InvSortCol) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  // Fetch invoices from API
  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async (pageArg = 1) => {
    try {
      setLoading(true);
      const response = await invoicesApi.list({ page: pageArg, per_page: 1000 });
      const paginator = response.data; // Laravel paginator: { data, current_page, last_page }
      const invoicesData = paginator?.data || paginator || [];
      setInvoices(Array.isArray(invoicesData) ? invoicesData : []);
      setPage(paginator?.current_page || pageArg);
      setLastPage(paginator?.last_page || 1);
    } catch (error) {
      showToast("Failed to load invoices", "error");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let result = invoices.filter(i => matchesStatus(i, activeFilter));

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(inv =>
        (inv.invoice_number || String(inv.id)).toLowerCase().includes(query) ||
        (inv.client?.name || "").toLowerCase().includes(query) ||
        (inv.event?.name || "").toLowerCase().includes(query)
      );
    }

    if (sortCol) {
      result = [...result].sort((a, b) => {
        let cmp = 0;
        if (sortCol === "invoice") cmp = (a.invoice_number || String(a.id)).localeCompare(b.invoice_number || String(b.id));
        else if (sortCol === "client") cmp = (a.client?.name || "").localeCompare(b.client?.name || "");
        else if (sortCol === "date") cmp = (a.invoice_date || "").localeCompare(b.invoice_date || "");
        else if (sortCol === "total") cmp = (Number(a.final_amount) || 0) - (Number(b.final_amount) || 0);
        else if (sortCol === "paid") cmp = (Number(a.paid_amount) || 0) - (Number(b.paid_amount) || 0);
        else if (sortCol === "balance") cmp = (Number(a.balance_amount) || 0) - (Number(b.balance_amount) || 0);
        return sortDir === "asc" ? cmp : -cmp;
      });
    } else {
      // No column chosen: newest-created invoices first.
      result = [...result].sort(byNewest);
    }

    return result;
  }, [activeFilter, invoices, searchQuery, sortCol, sortDir]);

const handleFormSubmit = async (data: any) => {
    try {
      if (editingInvoice) {
        // Only the due date and notes can change: amounts come from the event and
        // the status follows the payments recorded on it.
        await invoicesApi.update(editingInvoice.id, {
          due_date: data.dueDate || undefined,
          notes: data.notes || undefined,
        });
        showToast("Invoice updated", "success");
      } else {
        // New invoices are generated from a real event; the server computes the
        // amounts from that event's services, vendors and payments.
        const eventId = Number(data.event);
        if (!eventId) {
          showToast("Please select a valid event", "error");
          return;
        }
        const res = await invoicesApi.generateFromEvent(eventId, { notes: data.notes || undefined });
        showToast(res?.message || "Invoice generated", res?.already_existed ? "info" : "success");
      }
      setIsModalOpen(false);
      setEditingInvoice(null);
      loadInvoices();
    } catch (error: any) {
      showToast(error?.message || "Failed to save invoice", "error");
    }
  };

  const exportToExcel = () => {
    const rows = [
      ["#", "Invoice #", "Client", "Event", "Date", "Total (₹)", "Paid (₹)", "Balance (₹)", "Status"],
      ...filtered.map((inv, i) => [
        i + 1,
        inv.invoice_number || `INV-${String(inv.id).padStart(4, "0")}`,
        inv.client?.name || "",
        inv.event?.name || "",
        inv.invoice_date || "",
        Number(inv.final_amount) || 0,
        Number(inv.paid_amount) || 0,
        Number(inv.balance_amount) || 0,
        inv.status || "",
      ]),
      ["TOTAL", "", "", "", "",
        filtered.reduce((s, i) => s + (Number(i.final_amount) || 0), 0),
        filtered.reduce((s, i) => s + (Number(i.paid_amount) || 0), 0),
        filtered.reduce((s, i) => s + (Number(i.balance_amount) || 0), 0),
        "",
      ],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 5 }, { wch: 16 }, { wch: 22 }, { wch: 24 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 10 }];
    ws["!views"] = [{ state: "frozen", xSplit: 0, ySplit: 1, topLeftCell: "A2", activeCell: "A2", sqref: "A2" }];
    const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
    for (let r = 1; r <= range.e.r; r++) {
      ["F", "G", "H"].forEach(col => {
        const ref = `${col}${r + 1}`;
        if (ws[ref] && typeof ws[ref].v === "number") ws[ref].z = "#,##0.00";
      });
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Invoices");
    XLSX.writeFile(wb, `invoices_${new Date().toISOString().split("T")[0]}.xlsx`);
    showToast(`Exported ${filtered.length} invoices to Excel`, "success");
  };

  // Print invoice using browser's print functionality
  const handlePrintInvoice: (inv: Invoice) => void = (inv) => {
    setPrintInvoice(inv);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintInvoice(null), 100);
    }, 100);
  };

  // Hook for after-print notification
  useEffect(() => {
    const handleAfterPrint = () => {
      if (printInvoice) {
        showToast("Invoice ready - use browser's save as PDF option", "success");
      }
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => window.removeEventListener('afterprint', handleAfterPrint);
  }, [printInvoice, showToast]);

  const sendEmail = async () => {
    if (!emailRecipient || !emailingInvoice) return;
    
    setEmailStatus("sending");
    try {
      const result = await sendInvoiceEmail(emailRecipient, {
        id: emailingInvoice.invoice_number || String(emailingInvoice.id),
        client: emailingInvoice.client?.name || "Unknown",
        event: emailingInvoice.event?.name || "-",
        total: emailingInvoice.total_amount,
        final: emailingInvoice.final_amount,
        balance: emailingInvoice.balance_amount,
        dueDate: emailingInvoice.due_date,
        business: {
          name: settings?.business?.businessName,
          phone: settings?.business?.phone,
          email: settings?.business?.email,
        },
      });
      if (result.success) {
        setEmailStatus("success");
        showToast("Email sent successfully", "success");
      } else {
        setEmailStatus("error");
        setEmailError(result.error || "Failed to send email");
      }
    } catch (error) {
      setEmailStatus("error");
      showToast("Failed to send email", "error");
    }
  };

  const statusConfig = {
    paid: { color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
    partial: { color: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
    unpaid: { color: "bg-rose-50 text-rose-700 border-rose-200", icon: AlertCircle },
    cancelled: { color: "bg-stone-100 text-stone-500 border-stone-200", icon: AlertCircle },
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  // Shared by the desktop table and the mobile card list.
  const emptyState = (
    <>
      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-stone-100 flex items-center justify-center">
        <FileText className="w-6 h-6 text-stone-300" />
      </div>
      <h3 className="text-sm font-semibold text-stone-700">No invoices found</h3>
      <p className="text-xs text-stone-400 mt-1">Try adjusting filters or create a new invoice</p>
    </>
  );

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold text-stone-800">Invoices</h1>
          <p className="text-xs text-stone-500">{filtered.length} invoices • {formatCurrencyFull(invoices.filter(i => i.status !== "cancelled").reduce((s, i) => s + (Number(i.final_amount) || 0), 0))} billed • {formatCurrencyFull(invoices.reduce((s, i) => s + (Number(i.balance_amount) || 0), 0))} to collect</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={exportToExcel}
            disabled={filtered.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-white border border-stone-200 text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-40"
            title="Export to Excel"
          >
            <Sheet className="w-4 h-4" />
            Excel
          </button>
          <button
            onClick={() => { setEditingInvoice(null); setIsModalOpen(true); }}
            className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition-all"
          >
            <Plus className="w-4 h-4" />
            Generate
          </button>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row gap-3 mb-3">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            placeholder="Search invoices, clients..."
            aria-label="Search invoices"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-stone-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm"
          />
        </div>

        {/* Status Filters */}
        <div className="flex gap-1.5 overflow-x-auto">
          {statuses.map((s) => {
            const count = invoices.filter(i => matchesStatus(i, s)).length;
            if (count === 0 && (s === "cancelled" || s === "overdue") && activeFilter !== s) return null;
            const isActive = activeFilter === s;
            return (
              <button
                key={s}
                onClick={() => setActiveFilter(s)}
                className={`px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                  isActive
                    ? "bg-stone-800 text-white"
                    : "bg-white text-stone-600 border border-stone-200 hover:bg-stone-50"
                }`}
              >
                {STATUS_LABEL[s]}
                <span className={`ml-1.5 px-1 py-0.5 rounded text-[10px] ${isActive ? "bg-white/20" : "bg-stone-100"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Compact Table - Payment Details Focus */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-200">
                <InvSortTh col="invoice" label="Invoice" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <InvSortTh col="client" label="Client" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <InvSortTh col="total" label="Total" align="right" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <InvSortTh col="paid" label="Paid" align="right" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <InvSortTh col="balance" label="Balance" align="right" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <th className="text-center px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase">Status</th>
                <th className="text-right px-3 py-2.5 text-xs font-semibold text-stone-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {filtered.map((inv) => {
                const StatusIcon = statusConfig[inv.status as keyof typeof statusConfig]?.icon || Clock;
                const isOverdue = Boolean(inv.is_overdue);
                
                return (
                  <tr key={inv.id} className="hover:bg-stone-50/50 transition-colors">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                          <div className="font-medium text-stone-800 text-sm">{inv.invoice_number || `INV-${String(inv.id).padStart(4, "0")}`}</div>
                          <div className="text-xs text-stone-400">{inv.invoice_date}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-stone-700 text-sm truncate max-w-[120px]">{inv.client?.name || "—"}</div>
                      <div className="text-xs text-stone-400 truncate max-w-[120px]">{inv.event?.name || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="font-medium text-stone-800">{formatCurrency(inv.final_amount)}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="font-medium text-emerald-600">{formatCurrency(inv.paid_amount || 0)}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {inv.balance_amount > 0 ? (
                        <div className="font-medium text-rose-600">{formatCurrency(inv.balance_amount)}</div>
                      ) : inv.status === "cancelled" ? (
                        <div className="text-xs text-stone-400">—</div>
                      ) : (
                        <div className="font-medium text-emerald-600 text-xs">✓ Paid</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${statusConfig[inv.status as keyof typeof statusConfig]?.color || "bg-stone-100 text-stone-600"}`}>
                        <StatusIcon className="w-3 h-3" />
                        <span>{STATUS_LABEL[inv.status] || inv.status}</span>
                      </div>
                      {isOverdue && <div className="text-[10px] text-rose-600 mt-0.5">Overdue</div>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-0.5">
                        <button 
                          onClick={() => handlePrintInvoice(inv)}
                          className="p-1.5 rounded text-stone-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                          title="Download PDF"
                          aria-label="Download PDF"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => { setEmailingInvoice(inv); setIsEmailModalOpen(true); }}
                          className="p-1.5 rounded text-stone-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Email"
                          aria-label="Email invoice"
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => { setEditingInvoice(inv); setIsModalOpen(true); }}
                          className="p-1.5 rounded text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
                          title="Edit"
                          aria-label="Edit invoice"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        {inv.balance_amount > 0 && inv.event?.id && (
                          <button
                            onClick={() => navigate(`/events/detail?id=${inv.event!.id}`)}
                            className="ml-1 px-2.5 py-1 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 whitespace-nowrap"
                            title="Open the event to record a payment"
                          >
                            Take {formatCurrency(inv.balance_amount)}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-12">{emptyState}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards (< md) */}
        <div className="md:hidden divide-y divide-stone-100">
          {filtered.map((inv) => {
            const StatusIcon = statusConfig[inv.status as keyof typeof statusConfig]?.icon || Clock;
            const isOverdue = Boolean(inv.is_overdue);
            const invNo = inv.invoice_number || `INV-${String(inv.id).padStart(4, "0")}`;

            return (
              <div key={inv.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-stone-800 truncate">{invNo}</p>
                    <p className="text-xs text-stone-500 mt-0.5 truncate">
                      {[inv.client?.name, inv.event?.name].filter(Boolean).join(" · ") || "—"}
                    </p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${statusConfig[inv.status as keyof typeof statusConfig]?.color || "bg-stone-100 text-stone-600"}`}>
                      <StatusIcon className="w-3 h-3" />
                      <span>{STATUS_LABEL[inv.status] || inv.status}</span>
                    </div>
                    {isOverdue && <div className="text-[10px] text-rose-600 mt-0.5">Overdue</div>}
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div className="min-w-0">
                    <p className="text-xs text-stone-400">Date</p>
                    <p className="text-stone-700 truncate">{inv.invoice_date || "—"}</p>
                  </div>
                  <div className="min-w-0 text-right">
                    <p className="text-xs text-stone-400">Total</p>
                    <p className="font-semibold text-stone-800 truncate">{formatCurrency(inv.final_amount)}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-stone-400">Paid</p>
                    <p className="font-medium text-emerald-600 truncate">{formatCurrency(inv.paid_amount || 0)}</p>
                  </div>
                  <div className="min-w-0 text-right">
                    <p className="text-xs text-stone-400">Balance</p>
                    {inv.balance_amount > 0 ? (
                      <p className="font-medium text-rose-600 truncate">{formatCurrency(inv.balance_amount)}</p>
                    ) : inv.status === "cancelled" ? (
                      <p className="text-stone-400">—</p>
                    ) : (
                      <p className="font-medium text-emerald-600">✓ Paid</p>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                  <button
                    onClick={() => handlePrintInvoice(inv)}
                    className="h-10 min-w-10 inline-flex items-center justify-center rounded-lg border border-stone-200 text-stone-500 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                    title="Download PDF"
                    aria-label="Download PDF"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => { setEmailingInvoice(inv); setIsEmailModalOpen(true); }}
                    className="h-10 min-w-10 inline-flex items-center justify-center rounded-lg border border-stone-200 text-stone-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                    title="Email"
                    aria-label="Email invoice"
                  >
                    <Mail className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => { setEditingInvoice(inv); setIsModalOpen(true); }}
                    className="h-10 min-w-10 inline-flex items-center justify-center rounded-lg border border-stone-200 text-stone-500 hover:text-stone-700 hover:bg-stone-100 transition-colors"
                    title="Edit"
                    aria-label="Edit invoice"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  {inv.balance_amount > 0 && inv.event?.id && (
                    <button
                      onClick={() => navigate(`/events/detail?id=${inv.event!.id}`)}
                      className="h-10 px-3 inline-flex items-center justify-center rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700"
                    >
                      Take {formatCurrency(inv.balance_amount)}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && <div className="text-center py-12 px-4">{emptyState}</div>}
        </div>
        {lastPage > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-3 border-t border-stone-100">
            <span className="text-xs text-stone-500">Page {page} of {lastPage}</span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => loadInvoices(page - 1)}
                disabled={page <= 1}
                className="px-3 py-1.5 min-h-10 md:min-h-0 text-sm rounded-lg border border-stone-200 text-stone-600 disabled:opacity-40 hover:bg-stone-50"
              >Previous</button>
              <button
                onClick={() => loadInvoices(page + 1)}
                disabled={page >= lastPage}
                className="px-3 py-1.5 min-h-10 md:min-h-0 text-sm rounded-lg border border-stone-200 text-stone-600 disabled:opacity-40 hover:bg-stone-50"
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Email Modal */}
      <Modal
        isOpen={isEmailModalOpen}
        onClose={() => { setIsEmailModalOpen(false); setEmailingInvoice(null); setEmailRecipient(""); setEmailStatus("idle"); setEmailError(""); }}
        title="Send Invoice via Email"
        size="md"
      >
        {emailingInvoice && (
          <div className="space-y-4">
            <div className="bg-stone-50 p-3 rounded-lg">
              <div className="text-sm font-semibold text-stone-800">{emailingInvoice.invoice_number || emailingInvoice.id}</div>
              <div className="text-xs text-stone-500">{emailingInvoice.client?.name || "Unknown"}</div>
              <div className="text-sm font-bold text-amber-600 mt-1">{formatCurrencyFull(emailingInvoice.final_amount || 0)}</div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">Recipient Email</label>
              <input
                type="email"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder="customer@example.com"
                className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm"
              />
            </div>

            {emailStatus === "success" && (
              <div className="p-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Email sent successfully!
              </div>
            )}

            {emailStatus === "error" && (
              <div className="p-2 bg-rose-50 text-rose-700 rounded-lg text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {emailError || "Failed to send email"}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setIsEmailModalOpen(false)}
                className="flex-1 px-3 py-2 rounded-lg border border-stone-200 text-stone-600 font-medium text-sm hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                onClick={sendEmail}
                disabled={emailStatus === "sending" || !emailRecipient}
                className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 text-white font-medium text-sm hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {emailStatus === "sending" ? (
                  <>
                    <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                    Sending...
                  </>
                ) : "Send Email"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Add/Edit Invoice Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingInvoice(null); }}
        title={editingInvoice ? "Edit Invoice" : "Generate New Invoice"}
        size="lg"
      >
        <InvoiceForm
          invoice={editingInvoice ? {
            number: editingInvoice.invoice_number,
            client: editingInvoice.client?.name || "",
            event: editingInvoice.event?.name || "",
            total: Number(editingInvoice.final_amount) || 0,
            paid: Number(editingInvoice.paid_amount) || 0,
            balance: Number(editingInvoice.balance_amount) || 0,
            status: STATUS_LABEL[editingInvoice.status] || editingInvoice.status,
            dueDate: editingInvoice.due_date || "",
            notes: editingInvoice.notes || "",
          } : undefined}
          invoicedEventIds={invoices.map(i => i.event_id)}
          onSubmit={handleFormSubmit}
          onCancel={() => { setIsModalOpen(false); setEditingInvoice(null); }}
        />
      </Modal>

      {printInvoice && (
        <PrintableInvoice
          invoice={printInvoice}
          businessInfo={{
            name: settings?.business?.businessName || 'Shalimar',
            logoUrl: settings?.logoUrl,
            tagline: settings?.business?.tagline,
            address: settings?.business?.address,
            phone: settings?.business?.phone,
            email: settings?.business?.email,
            gstNumber: settings?.business?.gstNumber,
          }}
        />
      )}
    </DashboardLayout>
  );
}
